import { useEffect, useState } from 'react';
import { emptyStory, storyAssetUrl, storyDuration, type StoryDocument } from '../shared/story';
import { storyApi } from './story-api';

export function StoryEditor({
  csrf,
  onBusy,
  onSaved,
  onPreview,
  initial,
}: {
  initial: StoryDocument | null;
  csrf: string;
  onBusy: (value: boolean) => void;
  onSaved: (document: StoryDocument) => void;
  onPreview: (document: StoryDocument) => void;
}) {
  const [doc, setDoc] = useState<StoryDocument>(
    initial ?? { settings: structuredClone(emptyStory), assets: [] },
  );
  const [loading, setLoading] = useState(!initial);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (initial) return;
    let active = true;
    void storyApi
      .edit()
      .then((value) => {
        if (active) setDoc(value);
      })
      .catch((cause) => {
        if (active) setError(cause.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [initial]);
  const lock = (value: boolean) => {
    setBusy(value);
    onBusy(value);
  };
  async function upload(kind: 'powerpoint' | 'video' | 'audio', file?: File) {
    if (!file || busy) return;
    const limit = kind === 'video' ? 512 : 100;
    if (file.size > limit * 1024 * 1024) {
      setError(`Choose a file smaller than ${limit} MB.`);
      return;
    }
    lock(true);
    setError('');
    setMessage('');
    setProgress(0);
    try {
      const imported = await storyApi.upload(kind, file, csrf, setProgress);
      setDoc((current) => {
        const settings = { ...current.settings };
        if (kind === 'powerpoint') {
          settings.slides = imported.assets
            .filter((asset) => asset.kind === 'slide')
            .map((asset, index) => ({ assetId: asset.id, seconds: imported.slideSeconds[index] ?? 7 }));
          settings.mode = 'slides';
          if (imported.suggestedTracks.length) settings.tracks = imported.suggestedTracks;
        } else if (kind === 'video') {
          settings.videoId = imported.assets[0].id;
          settings.mode = 'video';
          settings.videoSound = true;
        } else {
          const last = settings.tracks.at(-1);
          const lastAsset = current.assets.find((asset) => asset.id === last?.assetId);
          const startSeconds =
            last && lastAsset ? last.startSeconds + lastAsset.duration - last.offsetSeconds : 0;
          settings.tracks = [
            ...settings.tracks,
            { assetId: imported.assets[0].id, startSeconds, offsetSeconds: 0, volume: 1 },
          ];
        }
        return { settings, assets: [...current.assets, ...imported.assets] };
      });
      setMessage(
        kind === 'powerpoint'
          ? `Imported ${imported.assets.filter((asset) => asset.kind === 'slide').length} slides and ${imported.suggestedTracks.length} embedded songs. Review the timings and music before saving. Animations and complex audio cues are not reproduced; use the PowerPoint-exported MP4 for those.`
          : 'File prepared. Preview it, then save your story settings.',
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The import did not finish. Please retry.');
    } finally {
      lock(false);
    }
  }
  async function save() {
    lock(true);
    setError('');
    setMessage('');
    try {
      const next = await storyApi.save(doc.settings, csrf);
      setDoc(next);
      onSaved(next);
      setMessage(
        next.settings.visible
          ? 'Story saved and available from Lloyd’s portrait.'
          : 'Story saved. It is hidden from guests until you turn it on.',
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save the story.');
    } finally {
      lock(false);
    }
  }
  const update = (change: Partial<StoryDocument['settings']>) =>
    setDoc((current) => ({ ...current, settings: { ...current.settings, ...change } }));
  if (loading) return <p role="status">Loading story settings…</p>;
  return (
    <div className="story-editor">
      <p className="eyebrow">Family controls</p>
      <h2>Set up Lloyd’s story.</h2>
      <p>
        Keep both versions here and choose which one guests will watch. The video exported from PowerPoint
        preserves the original presentation most faithfully.
      </p>
      <fieldset disabled={busy}>
        <legend>Presentation</legend>
        <div className="story-import-options">
          <label className="story-file-button">
            Import PowerPoint
            <input
              type="file"
              accept=".pptx,.ppt"
              aria-label="Import PowerPoint"
              onChange={(event) => {
                void upload('powerpoint', event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </label>
          <label className="story-file-button">
            Upload MP4
            <input
              type="file"
              accept="video/mp4,.mp4"
              aria-label="Upload story MP4"
              onChange={(event) => {
                void upload('video', event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </label>
        </div>
        <p className="fine-print">
          PowerPoint up to 100 MB. MP4 up to 512 MB, with H.264 video and AAC sound. Linked songs must be
          supplied separately; embedded songs in .pptx files are imported.
        </p>
        <label htmlFor="story-mode">Guests will watch</label>
        <select
          id="story-mode"
          value={doc.settings.mode}
          onChange={(event) => update({ mode: event.target.value as 'slides' | 'video' })}
        >
          <option value="slides">PowerPoint slide story</option>
          <option value="video">MP4 video</option>
        </select>
        {doc.settings.videoId && <p>{doc.assets.find((asset) => asset.id === doc.settings.videoId)?.name}</p>}
        {doc.settings.mode === 'slides' && !!doc.settings.slides.length && (
          <>
            <label htmlFor="story-default-time">Set every slide to this many seconds</label>
            <input
              id="story-default-time"
              type="number"
              min={2}
              max={300}
              defaultValue={7}
              onChange={(event) => {
                const seconds = Number(event.target.value);
                if (seconds >= 2 && seconds <= 300)
                  update({ slides: doc.settings.slides.map((slide) => ({ ...slide, seconds })) });
              }}
            />
            <ol className="story-slide-settings">
              {doc.settings.slides.map((slide, index) => (
                <li key={`${slide.assetId}-${index}`}>
                  <img src={storyAssetUrl(slide.assetId)} alt={`Slide ${index + 1} preview`} loading="lazy" />
                  <label>
                    Slide {index + 1}
                    <input
                      aria-label={`Seconds for slide ${index + 1}`}
                      type="number"
                      min={2}
                      max={300}
                      value={slide.seconds}
                      onChange={(event) =>
                        update({
                          slides: doc.settings.slides.map((item, i) =>
                            i === index ? { ...item, seconds: Number(event.target.value) } : item,
                          ),
                        })
                      }
                    />
                    seconds
                  </label>
                </li>
              ))}
            </ol>
          </>
        )}
      </fieldset>
      <fieldset disabled={busy}>
        <legend>Music</legend>
        <p>
          Music follows the story position when someone skips. Set when each song begins; the next song
          replaces the previous one.
        </p>
        {doc.settings.mode === 'video' && (
          <>
            <label className="story-checkbox">
              <input
                type="checkbox"
                checked={doc.settings.videoSound}
                onChange={(event) => update({ videoSound: event.target.checked })}
              />
              Keep the MP4’s original sound
            </label>
            <label className="story-checkbox">
              <input
                type="checkbox"
                checked={doc.settings.musicOnVideo}
                onChange={(event) => update({ musicOnVideo: event.target.checked })}
              />
              Play the added songs with this video
            </label>
          </>
        )}
        <label className="story-file-button">
          Add a song
          <input
            type="file"
            accept=".mp3,.m4a,.aac,.wav,.ogg"
            aria-label="Add story song"
            onChange={(event) => {
              void upload('audio', event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </label>
        {doc.settings.tracks.map((track, index) => (
          <div className="story-track-settings" key={`${track.assetId}-${index}`}>
            <strong>
              {doc.assets.find((asset) => asset.id === track.assetId)?.name || `Song ${index + 1}`}
            </strong>
            <div className="story-track-fields">
              {(['startSeconds', 'offsetSeconds', 'volume'] as const).map((field) => (
                <label key={field}>
                  {field === 'startSeconds'
                    ? 'Starts in story at'
                    : field === 'offsetSeconds'
                      ? 'Skip intro'
                      : 'Volume'}
                  <input
                    type="number"
                    aria-label={`Song ${index + 1} ${field}`}
                    min={0}
                    max={field === 'volume' ? 1 : 7200}
                    step={field === 'volume' ? 0.1 : 1}
                    value={track[field]}
                    onChange={(event) =>
                      update({
                        tracks: doc.settings.tracks.map((item, i) =>
                          i === index ? { ...item, [field]: Number(event.target.value) } : item,
                        ),
                      })
                    }
                  />
                  {field === 'volume' ? '0 to 1' : 'seconds'}
                </label>
              ))}
            </div>
            <button
              className="text-button"
              onClick={() => update({ tracks: doc.settings.tracks.filter((_, i) => i !== index) })}
            >
              Remove song
            </button>
          </div>
        ))}
        {!doc.settings.tracks.length && (
          <p className="fine-print">No separate songs added. An MP4 can use its own soundtrack.</p>
        )}
      </fieldset>
      <label className="story-checkbox">
        <input
          type="checkbox"
          disabled={busy}
          checked={doc.settings.visible}
          onChange={(event) => update({ visible: event.target.checked })}
        />
        Show the story to guests
      </label>
      {busy && (
        <div role="status">
          <progress max={100} value={progress} />
          <p>
            {progress < 100
              ? `Uploading ${progress}%…`
              : 'Preparing the slides and media. Keep this window open…'}
          </p>
        </div>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="story-editor-notice" role="status">
          {message}
        </p>
      )}
      <div className="modal-actions">
        <button
          className="secondary-button"
          disabled={busy || !storyDuration(doc)}
          onClick={() => onPreview(doc)}
        >
          Preview story
        </button>
        <button className="button" disabled={busy} onClick={() => void save()}>
          Save story
        </button>
      </div>
    </div>
  );
}
